import * as yup from 'yup';

import { CONTENT_WIDGETS, type FieldDef } from '../types';

const autocompleteValue = yup.object({
  id: yup.string().required(),
  label: yup.string()
});

const requiredMessage = (field: FieldDef) => `${field.label} is required`;

const fieldSchema = (field: FieldDef): yup.Schema => {
  switch (field.widget) {
    case 'text':
    case 'textarea':
    case 'date': {
      const base = yup.string();
      return field.required ? base.required(requiredMessage(field)) : base;
    }
    case 'number': {
      const base = yup
        .number()
        .transform((value, original) => (original === '' || original === null ? undefined : value))
        .typeError(`${field.label} must be a number`);
      return field.required ? base.required(requiredMessage(field)) : base;
    }
    case 'checkbox': {
      const base = yup.boolean();
      return field.required ? base.required(requiredMessage(field)) : base;
    }
    case 'select': {
      const values = (field.options ?? []).map((option) => option.value);
      const single = field.allowOther ? yup.string() : yup.string().oneOf(values);
      if (field.multiple) {
        const base = yup.array().of(single);
        return field.required ? base.min(1, requiredMessage(field)) : base;
      }
      return field.required ? single.required(requiredMessage(field)) : single.nullable();
    }
    case 'radio': {
      const values = (field.options ?? []).map((option) => option.value);
      const single = field.allowOther ? yup.string() : yup.string().oneOf(values);
      return field.required ? single.required(requiredMessage(field)) : single.nullable();
    }
    case 'autocomplete': {
      if (field.multiple) {
        const base = yup.array().of(autocompleteValue);
        return field.required ? base.min(1, requiredMessage(field)) : base;
      }
      const base = autocompleteValue.nullable();
      return field.required
        ? base.test('required', requiredMessage(field), (value) => !!value?.id)
        : base;
    }
    case 'html_content':
    case 'image':
    case 'section_header':
      // Content widgets carry no answer; buildYupSchema filters them out
      // before calling fieldSchema, so this branch is unreachable.
      return yup.mixed();
  }
};

const buildYupSchema = (fields: FieldDef[]) => {
  // Content widgets carry no answer, so they have no entry in form_data.
  const answerFields = fields.filter((field) => !CONTENT_WIDGETS.includes(field.widget));
  return yup.object(
    Object.fromEntries(answerFields.map((field) => [field.key, fieldSchema(field)]))
  );
};

export default buildYupSchema;
